import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, retry, shareReplay, tap } from 'rxjs/operators';

import {
  InstanceDto,
  InstanceResponse,
  InstanceUpdateResponse,
} from '@plugins/shareprice-lib/instance';
import { Share, ShareDto } from '@plugins/shareprice-lib/share';

import poll from '../utils/poll';

export type UpdateShares = { amount: number; price: number };

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly http = inject(HttpClient);
  public readonly instance = signal<InstanceDto>(null);
  public readonly userShares = signal<ShareDto | null>(null);

  public readonly share$: Observable<Share> = poll(
    this.http.get<Share>('/api/instance/share'),
    5 * 60 * 1000,
    true,
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fetchInstance = (): Observable<InstanceDto> => {
    if (this.instance()) {
      return of(this.instance());
    }
    return this.http.get<InstanceResponse>('/api/instance').pipe(
      retry({ delay: 7000 }),
      map(({ instance, shares }) => {
        this.instance.set(instance);
        this.userShares.set(shares);
        return instance;
      }),
    );
  };

  public readonly updateInstance = (
    instance: Partial<InstanceDto>,
  ): Observable<InstanceUpdateResponse> => {
    return this.http
      .patch<InstanceUpdateResponse>('/api/instance', { instance })
      .pipe(tap(({ instance }) => this.instance.set(instance)));
  };

  public readonly updateShares = (
    shares: UpdateShares,
  ): Observable<ShareDto> => {
    return this.http
      .patch<ShareDto>('/api/share', { shares })
      .pipe(tap((shares) => this.userShares.set(shares)));
  };
}
